'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useWallet } from '@/lib/wallet/WalletContext';
import { fetchOpenOrders, cancelHyperliquidOrder } from '@/lib/hyperliquid/service';
import type { HLOpenOrder } from '@/lib/hyperliquid/service';

interface ActiveOrder {
  id: string;
  oid: number;
  assetIndex: number;
  symbol: string;
  side: 'Buy' | 'Sell';
  orderType: 'Limit';
  price: number;
  size: number;
  origSize: number;
  filled: number; // 0–100 %
  status: 'Open' | 'Partial';
  timestamp: number;
}

function normalizeOrder(o: HLOpenOrder, idx: number): ActiveOrder {
  const sz = parseFloat(o.sz);
  const origSz = parseFloat(o.origSz);
  const filledSz = origSz - sz;
  const filledPct = origSz > 0 ? Math.round((filledSz / origSz) * 100) : 0;

  return {
    id: `ord-${o.oid}`,
    oid: o.oid,
    assetIndex: idx, // will be resolved separately
    symbol: `${o.coin}/USDC`,
    side: o.side === 'B' ? 'Buy' : 'Sell',
    orderType: 'Limit',
    price: parseFloat(o.limitPx),
    size: sz,
    origSize: origSz,
    filled: filledPct,
    status: filledPct > 0 ? 'Partial' : 'Open',
    timestamp: o.timestamp,
  };
}

interface ActiveOrdersPanelProps {
  symbol?: string;
}

export default function ActiveOrdersPanel({ symbol }: ActiveOrdersPanelProps) {
  const { isConnected, walletAddress, walletType } = useWallet();
  const [isExpanded, setIsExpanded] = useState(true);
  const [orders, setOrders] = useState<ActiveOrder[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Asset index cache: coin → index
  const [assetIndexCache, setAssetIndexCache] = useState<Record<string, number>>({});

  const loadAssetIndexes = useCallback(async (coins: string[]) => {
    const missing = coins.filter(c => !(c in assetIndexCache));
    if (missing.length === 0) return;
    try {
      const res = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'meta' }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const universe: { name: string }[] = data?.universe ?? [];
      const newCache: Record<string, number> = { ...assetIndexCache };
      universe.forEach((u, i) => { newCache[u.name] = i; });
      setAssetIndexCache(newCache);
    } catch {
      // ignore
    }
  }, [assetIndexCache]);

  const loadOrders = useCallback(async () => {
    if (!isConnected || !walletAddress) {
      setOrders([]);
      return;
    }
    try {
      const raw = await fetchOpenOrders(walletAddress);
      const normalized = raw.map((o, i) => normalizeOrder(o, assetIndexCache[o.coin] ?? 0));
      setOrders(normalized);
      // Load asset indexes for any new coins
      const coins = [...new Set(raw.map(o => o.coin))];
      await loadAssetIndexes(coins);
    } catch {
      // silently ignore
    }
  }, [isConnected, walletAddress, assetIndexCache, loadAssetIndexes]);

  useEffect(() => {
    setIsLoading(true);
    loadOrders().finally(() => setIsLoading(false));
    const interval = setInterval(loadOrders, 5000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  const allSelected = orders.length > 0 && selected.size === orders.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(orders.map(o => o.id)));
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const cancelOrder = async (order: ActiveOrder) => {
    setCancelError(null);
    if (walletType !== 'metamask') {
      setCancelError('MetaMask required to cancel orders.');
      return;
    }
    setCancellingIds(prev => new Set(prev).add(order.id));
    try {
      const result = await cancelHyperliquidOrder({
        walletAddress,
        assetIndex: assetIndexCache[order.symbol.split('/')[0]] ?? 0,
        oid: order.oid,
      });
      if (result.success) {
        setOrders(prev => prev.filter(o => o.id !== order.id));
        setSelected(prev => { const n = new Set(prev); n.delete(order.id); return n; });
      } else {
        setCancelError(result.message);
      }
    } catch (err: any) {
      if (err.code === 4001 || err.message?.includes('rejected')) {
        setCancelError('Signature rejected.');
      } else {
        setCancelError(err.message || 'Failed to cancel order.');
      }
    } finally {
      setCancellingIds(prev => { const n = new Set(prev); n.delete(order.id); return n; });
    }
  };

  const cancelSelected = async () => {
    const toCancel = orders.filter(o => selected.has(o.id));
    for (const order of toCancel) {
      await cancelOrder(order);
    }
  };

  const fmtPrice = (p: number) =>
    p >= 1000
      ? p.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      : p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <>
      <div className="border-t border-[#2a2a2a] shrink-0" style={{ background: '#141414' }}>
        {/* Header bar */}
        <div
          className="flex items-center gap-3 px-3 py-2 border-b border-[#2a2a2a] cursor-pointer select-none"
          style={{ background: '#1a1a1a' }}
          onClick={() => setIsExpanded(v => !v)}
        >
          <span
            className="text-[#94A3B8] transition-transform duration-200"
            style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}
          >
            <Icon name="ChevronDownIcon" size={14} />
          </span>

          <span className="text-xs font-semibold text-[#F1F5F9] uppercase tracking-wider">Active Orders</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 text-[#94A3B8]" style={{ background: '#2a2a2a' }}>
            {orders.length}
          </span>

          {isExpanded && (
            <div className="ml-auto flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-3 h-3 accent-[#4c6ef4] cursor-pointer"
                />
                <span className="text-[10px] text-[#94A3B8]">Select All</span>
              </label>

              <button
                disabled={selected.size === 0}
                onClick={cancelSelected}
                className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: selected.size > 0 ? 'rgba(239,68,68,0.12)' : '#1e1e1e',
                  border: '1px solid',
                  borderColor: selected.size > 0 ? '#EF4444' : '#2a2a2a',
                  color: selected.size > 0 ? '#EF4444' : '#94A3B8',
                }}
              >
                <Icon name="XMarkIcon" size={11} />
                Cancel Selected {selected.size > 0 ? `(${selected.size})` : ''}
              </button>
            </div>
          )}
        </div>

        {cancelError && (
          <div className="px-3 py-1.5 text-[10px] text-[#EF4444] bg-[rgba(239,68,68,0.08)] border-b border-[#2a2a2a]">
            {cancelError}
          </div>
        )}

        {/* Table */}
        {isExpanded && (
          <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
            {!isConnected ? (
              <div className="flex items-center justify-center py-6 text-xs text-[#94A3B8]">
                Connect wallet to view orders
              </div>
            ) : isLoading && orders.length === 0 ? (
              <div className="flex items-center justify-center py-6 gap-2 text-xs text-[#94A3B8]">
                <div className="w-3 h-3 border border-[#4c6ef4] border-t-transparent animate-spin" />
                Loading orders…
              </div>
            ) : orders.length === 0 ? (
              <div className="flex items-center justify-center py-6 text-xs text-[#94A3B8]">
                No active orders
              </div>
            ) : (
              <table className="w-full text-[11px] font-mono border-collapse">
                <thead>
                  <tr style={{ background: '#1e1e1e', borderBottom: '1px solid #2a2a2a' }}>
                    <th className="w-6 px-2 py-1.5 text-left" />
                    <th className="px-2 py-1.5 text-left text-[#94A3B8] font-medium whitespace-nowrap">Symbol</th>
                    <th className="px-2 py-1.5 text-left text-[#94A3B8] font-medium">Side</th>
                    <th className="px-2 py-1.5 text-left text-[#94A3B8] font-medium whitespace-nowrap">Type</th>
                    <th className="px-2 py-1.5 text-right text-[#94A3B8] font-medium">Price</th>
                    <th className="px-2 py-1.5 text-right text-[#94A3B8] font-medium">Size</th>
                    <th className="px-2 py-1.5 text-right text-[#94A3B8] font-medium whitespace-nowrap">Filled %</th>
                    <th className="px-2 py-1.5 text-left text-[#94A3B8] font-medium">Status</th>
                    <th className="px-2 py-1.5 text-center text-[#94A3B8] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, idx) => (
                    <tr
                      key={order.id}
                      style={{
                        background: selected.has(order.id)
                          ? 'rgba(76,110,244,0.08)'
                          : '#242424',
                        borderBottom: '1px solid #2a2a2a',
                      }}
                      className="hover:bg-[rgba(76,110,244,0.05)] transition-colors"
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={selected.has(order.id)}
                          onChange={() => toggleSelect(order.id)}
                          className="w-3 h-3 accent-[#4c6ef4] cursor-pointer"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-[#F1F5F9] font-semibold whitespace-nowrap">{order.symbol}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className="px-1.5 py-0.5 text-[10px] font-bold"
                          style={{
                            background: order.side === 'Buy' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                            color: order.side === 'Buy' ? '#22C55E' : '#EF4444',
                          }}
                        >
                          {order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-[#94A3B8] whitespace-nowrap">{order.orderType}</td>
                      <td className="px-2 py-1.5 text-right text-[#F1F5F9] tabular-nums">{fmtPrice(order.price)}</td>
                      <td className="px-2 py-1.5 text-right text-[#F1F5F9] tabular-nums">{order.size.toFixed(4)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        <div className="flex items-center justify-end gap-1.5">
                          <div className="w-12 h-1 bg-[#2a2a2a] overflow-hidden">
                            <div
                              className="h-full bg-[#4c6ef4]"
                              style={{ width: `${order.filled}%` }}
                            />
                          </div>
                          <span className="text-[#94A3B8]">{order.filled}%</span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span
                          className="px-1.5 py-0.5 text-[10px] font-medium"
                          style={{
                            background: order.status === 'Partial' ? 'rgba(234,179,8,0.15)' : 'rgba(76,110,244,0.15)',
                            color: order.status === 'Partial' ? '#EAB308' : '#4c6ef4',
                          }}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => cancelOrder(order)}
                          disabled={cancellingIds.has(order.id)}
                          className="px-2 py-0.5 text-[10px] font-medium transition-all disabled:opacity-50"
                          style={{
                            background: 'rgba(239,68,68,0.1)',
                            border: '1px solid rgba(239,68,68,0.3)',
                            color: '#EF4444',
                          }}
                        >
                          {cancellingIds.has(order.id) ? '…' : 'Cancel'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  );
}
